/**
 * SDK上报的部分
 */
import metaData, {
  sdkLoadTime,
  setUserId as setMetaUserId,
  setProductName as setMetaProductName,
  getUserId,
  getProductName,
  extendMeta
} from './metaData';
import { getLogger } from './logger';
import { setDebugMode as setConfigDebugMode } from './config';
import { addOnUnloadCallback, consoleLog, getDigest, isCdnAssets } from './utils';
import {
  setAppAnalyzeUserId,
  extendExtraAttr,
  sendImmediately,
  radarEvent
} from './appAnalyzeUtils';
import { initActiveHeartBeat } from './activeHeartBeat';
import { flushLog } from './reporter';

const internalLogger = getLogger('global');

initActiveHeartBeat(function (event) {
  sendImmediately('CLICK', {
    action: 'DOCS_ACTIVE_HEARTBEAT',
    type: 'USER_OPERATION',
    params: {
      event
    }
  });
  internalLogger.log('DOCS_ACTIVE_HEARTBEAT', event);
});

export function setDebugMode(debugMode: boolean) {
  setConfigDebugMode(debugMode);
}

let stepSum = 0;
//setUserId & setProductName都执行后
function onSetUserAndProduct() {
  //setUser & setProductName 或者 setUser超时 & setProductName 或者  setUser超时后又setUser&setProductName
  if (stepSum >= 11) {
    //统计PV,todo: 上报userId统计uv
    const sdkInitCost = internalLogger.getTimeCost();
    internalLogger.statistic('sdkInit', sdkInitCost);
    const logData: any = {
      userId: getUserId() || 'unknow',
      productName: getProductName(),
      cost: sdkInitCost
    };

    //监控navigationStart离谱
    if (sdkLoadTime) {
      logData.nvStToSdkLoad = sdkLoadTime - window.performance.timing.navigationStart;
    }

    internalLogger.info('sdkInit', logData);
    consoleLog(`sdkInit:`, `${logData.userId}`);
  }
}

//10秒钟还未设置用户，依然触发上报sdkInit
const checkUserTimer = setTimeout(function () {
  stepSum += 1;
  onSetUserAndProduct();
  console.warn('[DocLogger]setUser超时');
}, 10 * 1000);

export function setUserId(userId: string) {
  clearTimeout(checkUserTimer);
  setMetaUserId(userId);
  setAppAnalyzeUserId(userId);
  stepSum += 1;
  onSetUserAndProduct();
}

export function setProductName(productName: string) {
  setMetaProductName(productName);
  extendExtraAttr({
    prod_name: productName
  });
  stepSum += 10;
  onSetUserAndProduct();
  internalLogger.log('logger-set-prodname', productName);
}

function assetsStatistics() {
  if (window.performance && window.performance.getEntriesByType) {
    const entries = window.performance.getEntriesByType('resource') || [];
    const logEntries: any[] = [];
    const transferEntryTimes: number[] = [];
    entries.forEach(item => {
      const entryItem = item as PerformanceResourceTiming;
      //静态资源
      if (entryItem.initiatorType === 'link' || entryItem.initiatorType === 'script') {
        const duration = Math.round(item.duration);
        const logEntry = {
          d: duration,
          n: item.name
        };
        // 资源加载时长（未命中缓存）
        if (
          entryItem.decodedBodySize !== 0 &&
          entryItem.transferSize !== 0 &&
          isCdnAssets(entryItem.name)
        ) {
          transferEntryTimes.push(entryItem.duration);
        }
        logEntries.push(logEntry);
        //加载资源时长超过2s的资源上报
        if (item.duration > 2000) {
          internalLogger.statistic('performance.entries.tooLong', duration, item.name);
        }
      }
    });

    if (transferEntryTimes) {
      const sum = transferEntryTimes.reduce((a, b) => a + b, 0);
      const avg = sum / transferEntryTimes.length || 0;

      radarEvent(
        {
          name: 'entries_load_duration_avg'
        },
        {
          duration: avg
        }
      );

      internalLogger.statistic('performance.entries.durationAvg', avg);
    }

    internalLogger.info('performance.entries', logEntries);
  }
}

//性能统计
function performanceStatistics() {
  if (window['performance']) {
    const itemArr = ['responseStart', 'domContentLoadedEventStart', 'loadEventStart'];
    const timing = window['performance']['timing'];
    const start = timing['navigationStart'];
    const performanceData: { [key: string]: number } = itemArr.reduce((cur, item) => {
      cur[item] = timing[item] - start;
      return cur;
    }, {});

    performanceData.navigationStart = start;

    internalLogger.info('performance', performanceData);

    if (window.performance && window.performance.getEntries) {
      const entries = window.performance.getEntries() || [];

      entries.forEach(item => {
        const entryItem = item as PerformanceResourceTiming;
        if (entryItem.name === 'first-paint') {
          internalLogger.timeCost('performance.fp', entryItem.startTime);
        } else if (entryItem.name === 'first-contentful-paint') {
          internalLogger.timeCost('performance.fcp', entryItem.startTime);
        } else if (entryItem.name === 'first-input') {
          internalLogger.timeCost('performance.firstInput', entryItem.startTime);
        }
      });
    }
  }
}


window.addEventListener('load', function () {
  performanceStatistics();
  setTimeout(() => {
    assetsStatistics();
  }, 2000);
  internalLogger.timeCost('performance.onload');
});

window.addEventListener('DOMContentLoaded', function () {
  internalLogger.timeCost('performance.DOMContentLoaded');
});

// 劫持 window onerror
function isScriptError(e: ErrorEvent) {
  // 有 message 基本上认为是脚本异常了
  return e && (e.message || e.lineno != null);
}

// function composedPath(path?: Element[]) {
//   if (!path) return '';

//   const eleList = path.map(ele => {
//     const { tagName = '', id, className } = ele;
//     let item = tagName.toLowerCase();
//     if (id) item += `#${id}`;
//     //svg中img的className是个svgString对象
//     if (className && className.split)
//       item += className
//         .split(' ')
//         .map(v => `.${v}`)
//         .join('');
//     return item;
//   });

//   // window 取不出来，感觉意义不大，直接过滤掉吧
//   return eleList.filter(v => v).join(',');
// }

/**
 * 是否忽略error上报，返回true则忽略
 */
type ErrorFilter = (e: ErrorEvent) => boolean;

const ErrorFilterList: ErrorFilter[] = [];

export function ignoreError(errorFilter: ErrorFilter) {
  ErrorFilterList.push(errorFilter);
}

/**
 * addOnUnloadCallback优先使用onpagehide事件
 * safari下在没有kxid请求时会进入页面然后快速跳走，此过程不会触发onpagehide，但是会记录静态资源加载报错（可以认为是误报）
 * 这里通过beforeunload事件加一个标志位尝试标识误报
 * ios中使用onpagehide事件，不会触发beforeunload。且时序有问题。
 * android?
 */
window.addEventListener('beforeunload', () => {
  extendMeta({
    isUnload: 'beforeunload'
  });
});

window.addEventListener(
  'error',
  (e: ErrorEvent) => {
    try {
      try {
        const needIgnore = !!ErrorFilterList.find(errorFilter => {
          return typeof errorFilter === 'function' && errorFilter(e);
        });
        if (needIgnore) {
          return;
        }
      } catch (e) {
        internalLogger.error('window.errorFilter.error', e);
      }

      const scriptError = isScriptError(e);
      const error = e.error;

      // 获取触发错误的目标元素
      const target = e.target || e.srcElement;

      // 检查目标元素是否是 preload 标签
      if (target instanceof HTMLLinkElement && target.rel === 'preload') {
        //资源异常
        // 如果是 preload 标签，单独上报该错误
        let fileSrc = '';
        if ((e.target as any).src) {
          fileSrc = (e.target as any).src;
        } else if ((e.target as any).href) {
          fileSrc = (e.target as any).href;
        }

        const logSrcStr = `${fileSrc}#isUnload=${
          (metaData.gotoSSO && 'gotoSSO') || metaData.isUnload
        }`;
        internalLogger.error('error.preload_assetsError', fileSrc);
        radarEvent({ name: 'preload_assetsError', src: fileSrc });
        internalLogger.countWithDigest('error.preload_assetsError', logSrcStr);

        // 如果是 preload 标签，忽略该错误
        return;
      }

      //脚本错误
      if (scriptError) {
        const filename = e.filename;
        const stack = error ? error.stack || '' : '';
        const msg = e.message || '';

        const logInfo: Record<string, any> = {
          msg,
          stack,
          file: filename,
          line: e.lineno,
          col: e.colno
        };

        // 防止日志量过大影响公司服务，限制摘要长度最长50
        const errorDigest =
          //干掉无用报错前缀 ： Uncaught ce: error:  Uncaught
          getDigest(msg.replace('Uncaught ce: error:', '').replace('Uncaught', ''), 50) || 'unKnow';

        // 插件报错
        if (
          (stack && stack.indexOf('chrome-extension') >= 0) ||
          filename.startsWith('chrome-extension')
        ) {
          internalLogger.error('extensionError', errorDigest, logInfo);
        } else {
          // 脚本报错
          internalLogger.error('window.error.scriptError', errorDigest, logInfo);
          // 统计数据上报摘要
          internalLogger.countWithDigest('error.scriptError', errorDigest);
        }
      } else if (e && e.target) {
        //资源异常
        let fileSrc = '';
        if ((e.target as any).src) {
          fileSrc = (e.target as any).src;
        } else if ((e.target as any).href) {
          fileSrc = (e.target as any).href;
        }
        // 工程资源加载报错
        if (isCdnAssets(fileSrc)) {
          setTimeout(() => {
            const logSrcStr = `${fileSrc}#isUnload=${
              (metaData.gotoSSO && 'gotoSSO') || metaData.isUnload
            }`;
            internalLogger.error('window.error.assetsError', fileSrc);
            internalLogger.countWithDigest('error.assetsError', logSrcStr);
            radarEvent({ name: 'assetsError', src: fileSrc });
          });
        } else {
          internalLogger.error('window.error.resourceError', fileSrc);
          internalLogger.countWithDigest('error.resourceError', fileSrc);
        }
      }
    } catch (e) {
      console.error(e);
    }
  },
  true
);

window.addEventListener(
  'unhandledrejection',
  function (e) {
    const msg = e?.reason?.message;
    internalLogger.error('window.unhandledrejection', msg || '', {
      msg,
      reason: e ? e.reason : null
    });
  },
  true
);

function getFps(): Promise<number> {
  return new Promise(resolve => {
    let frame = 0;
    const lastTime = performance.now();
    function loop() {
      const now = performance.now();
      frame++;
      if (now > 1000 + lastTime) {
        const fps = Math.round((frame * 1000) / (now - lastTime));
        resolve(fps);
      } else {
        window.requestAnimationFrame(loop);
      }
    }
    loop();
  });
}

let minFps = 60;
const memoryArr: number[] = [];
let maxMemory = 0;
const supportPerformanceMemory = (window.performance as any)?.memory;

//开始统计fps和内存
async function startStatisticFpsAndMemory() {
  minFps = await getFps();
  if (supportPerformanceMemory) {
    const curMemory: number = (performance as any).memory.usedJSHeapSize;
    maxMemory = Math.max(maxMemory, curMemory);
    memoryArr.push(curMemory);
  }

  function getFpsInterval() {
    const interval = 1 * 60 * 1000;
    setTimeout(async () => {
      const fps = await getFps();
      minFps = Math.min(minFps, fps);
      getFpsInterval();
    }, interval);
  }
  getFpsInterval();
}

startStatisticFpsAndMemory();

// setTimeout(function(){
//     if(fpsArray.length){
//         const avgFps = fpsArray.reduce((sum,cur) => {
//             return sum + cur
//         },0)/fpsArray.length;
//         internalLogger.statistic('performance.fps',avgFps);
//     }
// },3000);

addOnUnloadCallback(function () {
  extendMeta({
    isUnload: 'OnUnload-1'
  });
  internalLogger.statistic('performance.fps', minFps);

  if (supportPerformanceMemory && memoryArr.length) {
    const memoryAvg = memoryArr.reduce((a, b) => a + b, 0) / memoryArr.length;
    internalLogger.statistic('performance.memory.avg', memoryAvg);
    internalLogger.statistic('performance.memory.max', maxMemory);
  }
  flushLog();
});
